import { useId, useState, type FormEvent } from 'react';
import { COMPANY } from '@sc/shared/content';
import { maskCelularInput, normalizeCelular } from '@sc/shared/domain';
import { TEXTOS } from '@sc/shared/triagem';
import { api, ApiError } from '../lib/api';

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

function erros(v: { nome: string; whatsapp: string; email: string }) {
  return {
    nome: v.nome.trim().length < 2 ? 'Escreva o seu nome.' : '',
    whatsapp: normalizeCelular(v.whatsapp) ? '' : 'Use o celular com DDD. Ex.: (11) 91234-5678.',
    email: !v.email.trim() || EMAIL.test(v.email.trim()) ? '' : 'Esse e-mail parece incompleto.',
  };
}

/** Formulário que aparece no fim da triagem. O link do WhatsApp só vem do servidor, depois do envio. */
export function ContactForm({ link, manutencao, onDone }: { link?: string | null; manutencao: boolean; onDone: (link: string) => void }) {
  const id = useId();
  const [v, setV] = useState({ nome: '', whatsapp: '', email: '' });
  const [consent, setConsent] = useState(false);
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [busy, setBusy] = useState(false);
  const [serverError, setServerError] = useState('');

  // Só abre WhatsApp: o link vem do servidor, mas confere o destino antes de mostrar.
  if (link && link.startsWith('https://wa.me/'))
    return (
      <section className="chat-form chat-form-done" aria-label="Continuar no WhatsApp">
        <p className="chat-form-ok">Pronto! Recebemos seus dados. Toque no botão para mandar o resumo e falar com {manutencao ? 'a equipe técnica' : 'um consultor'}.</p>
        <a className="chat-whatsapp" href={link} target="_blank" rel="noreferrer noopener">
          <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden="true" fill="currentColor">
            <path d="M12 2a10 10 0 0 0-8.6 15.1L2 22l5-1.3A10 10 0 1 0 12 2Zm0 18.2a8.2 8.2 0 0 1-4.2-1.2l-.3-.2-3 .8.8-2.9-.2-.3A8.2 8.2 0 1 1 12 20.2Zm4.5-6.1c-.2-.1-1.5-.7-1.7-.8s-.4-.1-.6.1-.7.8-.8 1-.3.2-.5.1a6.7 6.7 0 0 1-3.3-2.9c-.2-.4.2-.4.7-1.3.1-.2 0-.3 0-.4l-.8-1.8c-.2-.5-.4-.4-.6-.4h-.5a1 1 0 0 0-.7.3 3 3 0 0 0-.9 2.2 5.2 5.2 0 0 0 1.1 2.7 11.8 11.8 0 0 0 4.5 4c1.7.7 2.4.8 3.2.6a2.8 2.8 0 0 0 1.8-1.3 2.3 2.3 0 0 0 .2-1.3c-.1-.1-.3-.2-.5-.3Z" />
          </svg>
          {TEXTOS.botaoWhatsapp}
        </a>
        <p className="chat-form-note">Se preferir, a nossa equipe também pode chamar você no número informado.</p>
      </section>
    );

  const e = erros(v);
  const valido = !e.nome && !e.whatsapp && !e.email && consent;
  const show = (k: keyof typeof e) => (touched[k] && e[k] ? e[k] : '');

  async function submit(ev: FormEvent) {
    ev.preventDefault();
    setTouched({ nome: true, whatsapp: true, email: true });
    if (!valido || busy) return;
    setBusy(true);
    setServerError('');
    try {
      const r = await api.post<{ link: string }>('/chat/contact', {
        nome: v.nome.trim(),
        whatsapp: v.whatsapp,
        ...(v.email.trim() ? { email: v.email.trim() } : {}),
        consent: true,
      });
      onDone(r.link);
    } catch (err) {
      setServerError(err instanceof ApiError ? err.message : 'Não consegui enviar. Tente de novo.');
    } finally {
      setBusy(false);
    }
  }

  const field = (k: 'nome' | 'whatsapp' | 'email', label: string, props: React.InputHTMLAttributes<HTMLInputElement>) => (
    <div className="chat-field">
      <label htmlFor={`${id}-${k}`}>{label}</label>
      <input
        id={`${id}-${k}`}
        value={v[k]}
        onChange={(ev) => setV({ ...v, [k]: k === 'whatsapp' ? maskCelularInput(ev.target.value) : ev.target.value })}
        onBlur={() => setTouched((t) => ({ ...t, [k]: true }))}
        aria-invalid={!!show(k)}
        aria-describedby={show(k) ? `${id}-${k}-erro` : undefined}
        {...props}
      />
      {show(k) && (
        <span id={`${id}-${k}-erro`} className="chat-error" role="alert">
          {show(k)}
        </span>
      )}
    </div>
  );

  return (
    <form className="chat-form" onSubmit={submit} noValidate aria-label="Seus dados para continuar no WhatsApp">
      {field('nome', 'Seu nome', { type: 'text', autoComplete: 'name', maxLength: 120, required: true })}
      {field('whatsapp', 'WhatsApp', { type: 'tel', inputMode: 'numeric', autoComplete: 'tel-national', placeholder: '(11) 91234-5678', maxLength: 15, required: true })}
      {field('email', 'E-mail (opcional)', { type: 'email', inputMode: 'email', autoComplete: 'email', maxLength: 200 })}
      <label className="chat-check">
        <input type="checkbox" checked={consent} onChange={(ev) => setConsent(ev.target.checked)} />
        <span>
          {TEXTOS.consentimento}{' '}
          <a href={COMPANY.privacy} target="_blank" rel="noreferrer noopener">
            Política de privacidade
          </a>
        </span>
      </label>
      {!manutencao && <p className="chat-form-note">{TEXTOS.transparencia}</p>}
      {serverError && (
        <p className="chat-error" role="alert">
          {serverError}
        </p>
      )}
      <button className="chat-submit" type="submit" disabled={!valido || busy}>
        {busy ? 'Enviando…' : 'Enviar e continuar no WhatsApp'}
      </button>
    </form>
  );
}
